import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, X, Plus, Loader2, ChevronDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { attendanceService } from '@/services/attendance.service';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

// ── Types ──────────────────────────────────────────────────────────────────────

type Mode = 'disabled' | 'role_wise' | 'person_wise';

interface PersonGrant {
  id: string;
  granter_user_id: string; granter_first_name: string | null; granter_last_name: string | null; granter_employee_code: string | null;
  target_user_id: string;  target_first_name: string | null;  target_last_name: string | null;  target_employee_code: string | null;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const ROLES = [
  { code: 'SUPER_ADMIN',  label: 'Super Admin' },
  { code: 'ADMIN',        label: 'Admin' },
  { code: 'MANAGER',      label: 'Manager' },
  { code: 'TEAM_LEADER',  label: 'Team Leader' },
  { code: 'USER',         label: 'User' },
];

const MODE_OPTIONS: { value: Mode; label: string; description: string }[] = [
  { value: 'disabled',    label: 'Disabled',    description: 'Any user with attendance management permission can delete punch logs.' },
  { value: 'role_wise',   label: 'Role-wise',   description: 'Only users whose role is in the allowed list can delete punch logs.' },
  { value: 'person_wise', label: 'Person-wise', description: 'Only specifically assigned employees can delete, and only for their assigned targets.' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function fullName(first: string | null, last: string | null, code: string | null): string {
  const name = [first, last].filter(Boolean).join(' ') || '–';
  return code ? `${name} (${code})` : name;
}

function getInitials(first: string | null, last: string | null): string {
  return [(first ?? '').charAt(0), (last ?? '').charAt(0)].filter(Boolean).join('').toUpperCase() || '?';
}

// ── EmployeeCell ───────────────────────────────────────────────────────────────

function EmployeeCell({ first, last, code }: { first: string | null; last: string | null; code: string | null }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
        {getInitials(first, last)}
      </div>
      <div>
        <p className="text-xs font-medium text-foreground">
          {[first, last].filter(Boolean).join(' ') || '–'}
        </p>
        {code && <p className="text-[10px] text-muted-foreground">{code}</p>}
      </div>
    </div>
  );
}

// ── Employee Option type ───────────────────────────────────────────────────────

interface EmployeeOption {
  value: string;
  label: string;
}

// ── Single-select Dropdown ─────────────────────────────────────────────────────

function EmployeeSelect({ value, onChange, options, placeholder, excludeId }: {
  value: string;
  onChange: (id: string) => void;
  options: EmployeeOption[];
  placeholder: string;
  excludeId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(
    (o) => o.value !== excludeId && o.label.toLowerCase().includes(search.toLowerCase()),
  );
  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2.5 text-sm hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-ring/20"
      >
        <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-border/60 bg-background shadow-lg">
          <div className="p-2">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-lg border border-border/60 px-3 py-1.5 text-sm focus:border-primary/60 focus:outline-none"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-xs text-muted-foreground">No results</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-muted/30 ${o.value === value ? 'font-semibold text-primary' : 'text-foreground'}`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Multi-select Dropdown ──────────────────────────────────────────────────────

function EmployeeMultiSelect({ values, onChange, options, excludeId }: {
  values: string[];
  onChange: (ids: string[]) => void;
  options: EmployeeOption[];
  excludeId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const eligible = options.filter((o) => o.value !== excludeId);
  const filtered  = eligible.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));

  const toggle    = (id: string) =>
    onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);
  const selectAll = () => onChange(filtered.map((o) => o.value));
  const clearAll  = () => onChange([]);

  const selectedOptions = options.filter((o) => values.includes(o.value));
  const allSelected = eligible.length > 0 && values.length === eligible.length;
  const label =
    values.length === 0
      ? 'Select employees…'
      : allSelected
        ? `All employees (${values.length})`
        : `${values.length} employee${values.length !== 1 ? 's' : ''} selected`;

  const CHIP_LIMIT = 3;
  const visibleChips  = selectedOptions.slice(0, CHIP_LIMIT);
  const overflowCount = selectedOptions.length - CHIP_LIMIT;

  return (
    <div className="relative">
      {selectedOptions.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1">
          {allSelected ? (
            <span className="flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">
              All {values.length} employees
              <button type="button" onClick={clearAll} className="rounded-full hover:opacity-80">
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <>
              {visibleChips.map((o) => (
                <span
                  key={o.value}
                  className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                >
                  {o.label}
                  <button type="button" onClick={() => toggle(o.value)} className="rounded-full hover:opacity-70">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {overflowCount > 0 && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  +{overflowCount} more
                </span>
              )}
            </>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-3 py-2.5 text-sm hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-ring/20"
      >
        <span className={values.length > 0 ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-border/60 bg-background shadow-lg">
          <div className="space-y-1.5 p-2">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-lg border border-border/60 px-3 py-1.5 text-sm focus:border-primary/60 focus:outline-none"
            />
            <div className="flex gap-3 px-1">
              <button type="button" onClick={selectAll} className="text-xs text-primary hover:underline">
                Select all
              </button>
              <span className="text-border">|</span>
              <button type="button" onClick={clearAll} className="text-xs text-muted-foreground hover:underline">
                Clear all
              </button>
            </div>
          </div>
          <div className="max-h-52 divide-y divide-border/40 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-xs text-muted-foreground">No results</p>
            ) : (
              filtered.map((o) => {
                const checked = values.includes(o.value);
                return (
                  <label
                    key={o.value}
                    className="flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.value)}
                      className="h-4 w-4 rounded border-border/60 text-primary focus:ring-primary/30"
                    />
                    <span className={`text-sm ${checked ? 'font-semibold text-primary' : 'text-foreground'}`}>
                      {o.label}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Assignment Modal ───────────────────────────────────────────────────────

interface AddGrantModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (granterUserId: string, targetUserIds: string[]) => void;
  isPending: boolean;
}

function AddGrantModal({ open, onClose, onAdd, isPending }: AddGrantModalProps) {
  const [granterId, setGranterId] = useState('');
  const [targetIds, setTargetIds] = useState<string[]>([]);

  const { data: usersData } = useQuery({
    queryKey: ['users-list-for-grants'],
    queryFn: () => usersService.list({ limit: 100 }),
  });

  const options: EmployeeOption[] = (usersData?.data ?? []).map((u) => ({
    value: u.id,
    label: fullName(u.first_name, u.last_name, u.employee_code),
  }));

  useEffect(() => {
    if (usersData?.data) {
      setTargetIds(usersData.data.map((u) => u.id));
    }
  }, [usersData]);

  const handleGranterChange = (id: string) => {
    setGranterId(id);
    setTargetIds((prev) => prev.filter((t) => t !== id));
  };

  const handleOpenChange = (o: boolean) => {
    if (!o && !isPending) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Assignment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Who can delete?</label>
            <EmployeeSelect
              value={granterId}
              onChange={handleGranterChange}
              options={options}
              placeholder="Select employee…"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Can delete for which employees?</label>
            <EmployeeMultiSelect
              values={targetIds}
              onChange={setTargetIds}
              options={options}
              excludeId={granterId || undefined}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            disabled={!granterId || targetIds.length === 0 || isPending}
            onClick={() => onAdd(granterId, targetIds)}
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add ({targetIds.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Role-wise Panel ────────────────────────────────────────────────────────────

function RoleWisePanel({ allowedRoles }: { allowedRoles: string[] }) {
  const qc = useQueryClient();

  const addGrant = useMutation({
    mutationFn: (roleCode: string) => attendanceService.addPunchDeleteRoleGrant(roleCode),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['punch-delete-config'] }); toast.success('Role added'); },
    onError: () => toast.error('Failed to update'),
  });

  const removeGrant = useMutation({
    mutationFn: (roleCode: string) => attendanceService.removePunchDeleteRoleGrant(roleCode),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['punch-delete-config'] }); toast.success('Role removed'); },
    onError: () => toast.error('Failed to update'),
  });

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5 space-y-4">
      <div>
        <p className="text-sm font-semibold">Allowed Roles</p>
        <p className="text-xs text-muted-foreground mt-0.5">Only users with these roles can delete punch logs.</p>
      </div>
      <div className="space-y-2">
        {ROLES.map((role) => {
          const checked = allowedRoles.includes(role.code);
          const pending = addGrant.isPending || removeGrant.isPending;
          return (
            <label key={role.code} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border/40 px-4 py-3 hover:bg-muted/30 transition-colors">
              <input
                type="checkbox"
                checked={checked}
                disabled={pending}
                onChange={() => {
                  if (checked) removeGrant.mutate(role.code);
                  else addGrant.mutate(role.code);
                }}
                className="h-4 w-4 rounded border-border/60 text-primary focus:ring-primary/30"
              />
              <span className="text-sm font-medium">{role.label}</span>
              {checked && (
                <span className="ml-auto inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  Allowed
                </span>
              )}
            </label>
          );
        })}
      </div>
      {allowedRoles.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
          No roles selected — no one can delete punch logs in this mode.
        </div>
      )}
    </div>
  );
}

// ── Person-wise Panel ──────────────────────────────────────────────────────────

const GRANTS_PAGE_SIZE = 20;

function PersonWisePanel({ grants }: { grants: PersonGrant[] }) {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(grants.length / GRANTS_PAGE_SIZE));
  const pagedGrants = grants.slice((page - 1) * GRANTS_PAGE_SIZE, page * GRANTS_PAGE_SIZE);

  const addGrants = useMutation({
    mutationFn: async ({ granterUserId, targetUserIds }: { granterUserId: string; targetUserIds: string[] }) => {
      await Promise.all(
        targetUserIds.map((targetId) =>
          attendanceService.addPunchDeletePersonGrant(granterUserId, targetId),
        ),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['punch-delete-config'] });
      setShowModal(false);
      toast.success('Assignments added');
    },
    onError: () => toast.error('Failed to add assignments'),
  });

  const removeGrant = useMutation({
    mutationFn: (grantId: string) => attendanceService.removePunchDeletePersonGrant(grantId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['punch-delete-config'] }); toast.success('Assignment removed'); },
    onError: () => toast.error('Failed to remove'),
  });

  const columns: Column<PersonGrant>[] = [
    {
      key: 'granter',
      header: 'Can Delete',
      cell: (g) => <EmployeeCell first={g.granter_first_name} last={g.granter_last_name} code={g.granter_employee_code} />,
    },
    {
      key: 'target',
      header: 'For Employee',
      cell: (g) => <EmployeeCell first={g.target_first_name} last={g.target_last_name} code={g.target_employee_code} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (g) => (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => removeGrant.mutate(g.id)}
          disabled={removeGrant.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Person Assignments</p>
          <p className="text-xs text-muted-foreground mt-0.5">Each row grants one employee the right to delete punches for a specific target.</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4" /> Add Assignment
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={pagedGrants}
        rowKey={(g) => g.id}
        empty="No assignments yet — click Add Assignment to get started."
        pagination={grants.length > 0 ? {
          page,
          totalPages,
          total: grants.length,
          onPageChange: setPage,
        } : undefined}
      />

      <AddGrantModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onAdd={(granterId, targetIds) => addGrants.mutate({ granterUserId: granterId, targetUserIds: targetIds })}
        isPending={addGrants.isPending}
      />
    </div>
  );
}

// ── Main Section ───────────────────────────────────────────────────────────────

export function DeletePunchPermissionSection() {
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['punch-delete-config'],
    queryFn: () => attendanceService.getPunchDeleteConfig(),
  });

  const updateMode = useMutation({
    mutationFn: (mode: string) => attendanceService.updatePunchDeleteMode(mode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['punch-delete-config'] });
      void qc.invalidateQueries({ queryKey: ['my-punch-delete-permission'] });
      toast.success('Permission mode updated');
    },
    onError: () => toast.error('Failed to update mode'),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-muted/60" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm text-destructive">
        Failed to load permission config.
      </div>
    );
  }

  const { mode, allowed_roles, person_grants } = data;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/50 border border-border/60">
          <ShieldCheck className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h2 className="text-base font-semibold">Delete Punch Permission Control</h2>
          <p className="text-xs text-muted-foreground">Configure who can delete punch logs and for which employees.</p>
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-card p-5 space-y-3">
        <p className="text-sm font-semibold">Permission Mode</p>
        <div className="space-y-2">
          {MODE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3.5 transition-colors ${
                mode === opt.value
                  ? 'border-primary/40 bg-primary/5'
                  : 'border-border/60 bg-card hover:bg-muted/30'
              }`}
            >
              <input
                type="radio"
                name="punch-delete-mode"
                value={opt.value}
                checked={mode === opt.value}
                disabled={updateMode.isPending}
                onChange={() => updateMode.mutate(opt.value)}
                className="mt-0.5 h-4 w-4 text-primary focus:ring-primary/30"
              />
              <div>
                <p className={`text-sm font-semibold ${mode === opt.value ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {mode === 'role_wise'   && <RoleWisePanel   allowedRoles={allowed_roles} />}
      {mode === 'person_wise' && <PersonWisePanel grants={person_grants} />}
    </div>
  );
}
